/*!
 * Copyright (c) 2015-present, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * 
 * See the License for the specific language governing permissions and limitations under the License.
 */

import AuthSdkError from '../errors/AuthSdkError.js';
import { getRemediator, handleIdxError, isTerminalResponse, filterValuesForRemediation, getNextStep } from './util.js';
function getActionFromValues(values, idxResponse) {
  return Object.keys(idxResponse.actions).find(action => !!values.resend && action.includes('-resend'));
}
function removeActionFromValues(values) {
  return Object.assign(Object.assign({}, values), {
    resend: undefined
  });
}
function removeActionFromOptions(options, actionName) {
  let actions = options.actions || [];
  actions = actions.filter(entry => {
    if (typeof entry === 'string') {
      return entry !== actionName;
    }
    return entry.name !== actionName;
  });
  return Object.assign(Object.assign({}, options), {
    actions
  });
}
async function remediate(authClient, idxResponse, values, options) {
  let {
    neededToProceed,
    interactionCode
  } = idxResponse;
  const {
    flow
  } = options;
  if (interactionCode) {
    return {
      idxResponse
    };
  }
  const remediator = getRemediator(neededToProceed, values, options);
  const actionFromValues = getActionFromValues(values, idxResponse);
  const actionFromOptions = options.actions || [];
  const actions = [...actionFromOptions, ...(actionFromValues && [actionFromValues] || [])];
  if (actions) {
    for (let action of actions) {
      let params = {};
      if (typeof action !== 'string') {
        params = action.params || {};
        action = action.name;
      }
      let valuesWithoutExecutedAction = removeActionFromValues(values);
      let optionsWithoutExecutedAction = removeActionFromOptions(options, action);
      if (typeof idxResponse.actions[action] === 'function') {
        try {
          idxResponse = await idxResponse.actions[action](params);
          idxResponse = Object.assign(Object.assign({}, idxResponse), {
            requestDidSucceed: true
          });
        } catch (e) {
          return handleIdxError(authClient, e, options);
        }
        if (action === 'cancel') {
          return {
            idxResponse,
            canceled: true
          };
        }
        return remediate(authClient, idxResponse, valuesWithoutExecutedAction, optionsWithoutExecutedAction);
      }
      const remediationAction = neededToProceed.find(_ref => {
        let {
          name
        } = _ref;
        return name === action;
      });
      if (remediationAction) {
        try {
          idxResponse = await idxResponse.proceed(action, params);
          idxResponse = Object.assign(Object.assign({}, idxResponse), {
            requestDidSucceed: true
          });
        } catch (e) {
          return handleIdxError(authClient, e, options);
        }
        return remediate(authClient, idxResponse, values, optionsWithoutExecutedAction);
      }
    }
  }
  const terminal = isTerminalResponse(idxResponse);
  if (terminal) {
    return {
      idxResponse,
      terminal
    };
  }
  if (!remediator) {
    if (options.step) {
      values = filterValuesForRemediation(idxResponse, options.step, values);
      try {
        idxResponse = await idxResponse.proceed(options.step, values);
        idxResponse = Object.assign(Object.assign({}, idxResponse), {
          requestDidSucceed: true
        });
        return {
          idxResponse
        };
      } catch (e) {
        return handleIdxError(authClient, e, options);
      }
    }
    if (flow === 'default') {
      return {
        idxResponse
      };
    }
    throw new AuthSdkError("\n      No remediation can match current flow, check policy settings in your org.\n      Remediations: [".concat(neededToProceed.reduce((acc, curr) => acc ? acc + ' ,' + curr.name : curr.name, ''), "]\n    "));
  }
  if (!remediator.canRemediate()) {
    const nextStep = getNextStep(authClient, remediator, idxResponse);
    return {
      idxResponse,
      nextStep
    };
  }
  const name = remediator.getName();
  const data = remediator.getData();
  try {
    idxResponse = await idxResponse.proceed(name, data);
    idxResponse = Object.assign(Object.assign({}, idxResponse), {
      requestDidSucceed: true
    });
    values = remediator.getValuesAfterProceed();
    options = Object.assign(Object.assign({}, options), {
      step: undefined
    });
    if (options.useGenericRemediator && !idxResponse.interactionCode && !isTerminalResponse(idxResponse)) {
      const gr = getRemediator(idxResponse.neededToProceed, values, options);
      const nextStep = getNextStep(authClient, gr, idxResponse);
      return {
        idxResponse,
        nextStep
      };
    }
    return remediate(authClient, idxResponse, values, options);
  } catch (e) {
    return handleIdxError(authClient, e, options);
  }
}
export { remediate };